import React, { useState, ReactNode } from 'react';
import {
  Box,
  Flex,
  Icon,
  HStack,
  VStack,
  useDisclosure,
  useBreakpointValue,
  IconButton,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import { FaFile, FaUser, FaArrowRightFromBracket, FaCircleInfo, FaLightbulb, FaUpload, FaBook, FaDatabase, FaChartBar, FaPalette, FaKey, FaPlug } from 'react-icons/fa6';
import { Link, useLocation } from 'wouter';
import { useAuth } from '../hooks/useAuth';
import eyebrightFlower from '../assets/eyebright-flower.png';
import { FaBars } from "react-icons/fa6";
import { useCurrentUser } from '../hooks/useCurrentUser';
import WhatsNewModal from './WhatsNewModal';
import { ACTION_BAR_HEIGHT, ACTION_BAR_MARGIN, SIDEBAR_WIDTH, SIDEBAR_ITEM_HEIGHT } from '../util/layoutConstants';

interface SubmenuItem {
  href?: string;
  text: string;
  icon?: React.ElementType;
  onClick?: () => void;
}

interface MenuOption {
  name: string;
  href: string;
  // Exactly one of these two is set on every entry - icon for the normal react-icons case,
  // iconElement as an escape hatch for the one entry (Home) whose icon is an <img>, not a
  // component. Both Sidebar (desktop) and BurgerMenu (mobile) read from this one array, so
  // adding Home here (rather than hardcoding it separately in Sidebar like before) is what makes
  // it show up consistently on both, instead of desktop-only.
  icon?: React.ElementType;
  iconElement?: React.ReactNode;
  internalCustomerOnly?: boolean;
  hidden?: boolean;
  // Visible only to this one signed-in email, on top of any other gating - for a single-user
  // preview/testing entry, not a role. Compared case-insensitively against useCurrentUser()'s
  // emailaddress.
  restrictedToEmail?: string;
  submenu?: SubmenuItem[]; // general mechanism - no entry populates this today
}

const menuOptions: MenuOption[] = [
  {
    name: 'Home',
    href: '/home',
    iconElement: <img src={eyebrightFlower} alt="Eyebright Flower" width="20" />,
  },
  {
    name: 'Document Library',
    href: '/document_library',
    icon: FaFile,
    internalCustomerOnly: true,
  },
  {
    name: 'Upload Document',
    href: '/upload_document',
    icon: FaUpload,
    internalCustomerOnly: true,
  },
  {
    name: 'Account Mapping',
    href: '/account_mapping',
    icon: FaBook,
    internalCustomerOnly: true,
  },
  {
    name: 'Supplier Mapping',
    href: '/supplier_mapping',
    icon: FaPlug,
    internalCustomerOnly: true,
  },
  {
    name: 'Open Passwords',
    href: '/open_password',
    icon: FaKey,
    internalCustomerOnly: true,
  },
  {
    name: 'Reports',
    href: '/reports',
    icon: FaChartBar,
    internalCustomerOnly: true,
  },
  {
    name: 'Generic CRUD',
    href: '/generic_crud',
    icon: FaDatabase,
    internalCustomerOnly: true,
    // Testing only for now - visible only to this one login while the feature is still being
    // built out (see design/CRUD_engine_test_plan.md for current status). Still reachable by
    // anyone typing /generic_crud directly into the address bar - this only controls the NavBar
    // listing. Remove restrictedToEmail once it's ready for normal use.
    restrictedToEmail: 'k.hutton@eyebrightutilities.co.uk',
  },
]

interface NavItemProps {
  icon?: React.ElementType;
  // Escape hatch for a non-react-icons icon (e.g. the logo image) that still needs to render
  // through the exact same row/hover/flyout/active-state machinery as every other item below,
  // instead of duplicating that machinery for one special-cased item. Takes priority over `icon`.
  iconElement?: React.ReactNode;
  href?: string;
  text: string;
  color?: string;
  ticketsOpen?: boolean;
  renewalsOpen?: boolean;
  onClick?: () => void;
  submenu?: SubmenuItem[];
}

interface SidebarProps {
  children: ReactNode;
}

// Always-visible cue for the current page - shown regardless of hover, unlike the flyout label
// below which only appears on hover (text now only ever appears in the flyout, never inline).
// Flat bar, deliberately no border-radius of its own: a "md" (6px) radius on a 3px-wide box is
// geometrically invalid and rendered as a broken/flat edge (that's the "cut-off square" bug) -
// the parent HStack's own borderRadius is made asymmetric (square left / rounded right, see
// rowStyle below) to match this bar's flat edge instead, rather than two elements each trying to
// round the same corner independently.
const ActiveAccentBar: React.FC<{ color?: string }> = ({ color }) => (
  <Box position="absolute" left={0} top={0} bottom={0} w="3px" bg={color} />
);

// Plain floating label next to the hovered icon - deliberately a plain Box, not Tooltip/Popover
// ("ideally not really a pop-up"). Rendered as a normal child inside the sidebar's own
// position:fixed/zIndex:100000 container, so it inherits that stacking context for free and
// needs no zIndex override (avoids Chakra Tooltip's default-portal behaviour, which would
// otherwise render into the app's portalZIndex={2} container - well below page content).
// Same look regardless of the item's active state - deliberately not distinguishing active/
// inactive here, unlike the row itself (which already has the always-visible ActiveAccentBar).
const FlyoutLabel: React.FC<{ text: string; color?: string }> = ({ text, color }) => (
  <Box
    position="absolute"
    left="100%"
    top="50%"
    transform="translateY(-50%)"
    ml={2}
    whiteSpace="nowrap"
    bg={color}
    color="navbar-icon"
    borderRadius="md"
    px={3}
    py={1}
    boxShadow="sm"
  >
    {text}
  </Box>
);

const NavItem: React.FC<NavItemProps> = ({
  icon,
  iconElement,
  href,
  text,
  color,
  ticketsOpen,
  renewalsOpen,
  submenu,
  onClick
}) => {
  const [location] = useLocation();
  const subMenuIsActive = submenu && submenu.some(item => item.href === location);
  const isActive = !!(href === location || ticketsOpen || renewalsOpen || subMenuIsActive);
  const [isHovered, setIsHovered] = useState(false);
  const hasSubmenu = submenu && submenu.length > 0;

  // Icon-only row shared by both branches below - the rail never shows text inline, only ever
  // in the FlyoutLabel on hover. Deliberately NO explicit width here - it must size to its own
  // content (padding + icon) to exactly match SIDEBAR_WIDTH's derivation in layoutConstants.ts.
  // w="100%" was tried here and caused the row to overflow the fixed-width rail ("bulging out"):
  // 100% resolves against the parent's width, then margin is added ON TOP of that, not absorbed
  // by it - so w="100%" + m={1} is always wider than the rail, never equal to it.
  const rowStyle = {
    m: 1,
    bg: isActive ? "white" : color,
    p: 2,
    minH: SIDEBAR_ITEM_HEIGHT,
    // Square left corners when active (butts flush against the flat ActiveAccentBar), rounded
    // right corners always - two elements independently rounding the same corner is what caused
    // the "rounded right / cut-off square left" bug.
    borderLeftRadius: isActive ? 0 : "md",
    borderRightRadius: "md",
    position: "relative" as const,
    justifyContent: "center" as const,
  };

  if (hasSubmenu) {
    // Sub-items open a real click-to-choose menu next to the icon (never on hover - avoids the
    // classic accidental-trigger/close problem hover-driven multi-choice menus have, and works
    // for keyboard/touch). Hovering still shows this item's own FlyoutLabel like any other
    // button; it's suppressed while the menu is open so the label doesn't double up behind it.
    // NOTE: an entry with both href and submenu does NOT navigate to href on click - clicking
    // always opens the submenu instead.
    return (
      <Box data-testid={text}>
        <Menu placement="right-start" isLazy>
          {({ isOpen }) => (
            <>
              <MenuButton
                as={Box}
                cursor="pointer"
                onMouseOver={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <HStack {...rowStyle}>
                  {isActive && <ActiveAccentBar color={color} />}
                  {iconElement}
                  {!iconElement && icon && <Icon boxSize="20px" as={icon} color={isActive ? color : "navbar-icon"} />}
                  {isHovered && !isOpen && <FlyoutLabel text={text} color={color} />}
                </HStack>
              </MenuButton>
              <MenuList zIndex={100001}>
                {submenu!.map((item, index) =>
                  item.href ? (
                    <Link key={index} href={item.href}>
                      <MenuItem>{item.text}</MenuItem>
                    </Link>
                  ) : (
                    <MenuItem key={index} onClick={item.onClick}>{item.text}</MenuItem>
                  )
                )}
              </MenuList>
            </>
          )}
        </Menu>
      </Box>
    );
  }

  return (
    <Box
      data-testid={text}
      onMouseOver={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link href={href || "#"}>
        <HStack {...rowStyle} onClick={onClick}>
          {isActive && <ActiveAccentBar color={color} />}
          {iconElement}
          {!iconElement && icon && <Icon boxSize="20px" as={icon} color={isActive ? color : "navbar-icon"} />}
          {isHovered && <FlyoutLabel text={text} color={color} />}
        </HStack>
      </Link>
    </Box>
  );
};

const DynamicMenu: React.FC<SidebarProps> = ({ children }) => {
  // ssr: false matters (2026-10-07). Chakra's default (ssr: true) answers the FIRST render with
  // the `base` fallback and only then evaluates the media query, so on a desktop every page first
  // mounted under <BurgerMenu>, then remounted under <Sidebar> ~100 ms later - a different parent
  // type, so React threw the whole page away: every load-on-mount fetch ran twice (four times with
  // StrictMode in dev; the Upload Log and Document Library each fetched 4x per view). With
  // ssr: false the hook reads window.matchMedia synchronously and the first render is the right one.
  const type = useBreakpointValue(
    {
      base: 'BURGER',
      sm: 'BURGER',
      md: 'SIDEBAR',
    },
    { ssr: false }
  )

  if (type === 'SIDEBAR') {
    return (
      <Sidebar>
        {children}
      </Sidebar>
    )
  }

  if (type === 'BURGER') {
    return (
      <BurgerMenu>
        {children}
      </BurgerMenu>
    )
  }
}

const BurgerMenu: React.FC<SidebarProps> = ({ children }) => {
  const { isOpen: isWhatsNewOpen, onToggle: onWhatsNewToggle, onClose: onWhatsNewClose } = useDisclosure(); // tracks status of whats new modal
  const { user, customerType } = useCurrentUser();

  const showFlex = Number(user?.showFlex) > 0;

  const filteredOptions = menuOptions
  // Keep if not internal OR if the customer type is 'EYEBRIGHT'
  .filter(option => !option.internalCustomerOnly || customerType === 'EYEBRIGHT')
  // Keep if it's not 'Flex' OR (it is 'Flex' but showFlex is true)
  .filter(option => option.name !== 'Flex' || showFlex)
  // Keep if not explicitly hidden (testing-only pages still reachable by direct URL)
  .filter(option => !option.hidden)
  // Keep if not restricted to one email OR the signed-in user is that email
  .filter(option => !option.restrictedToEmail || user?.emailaddress?.toLowerCase() === option.restrictedToEmail.toLowerCase());

  return (
    <>
      <WhatsNewModal isOpen={isWhatsNewOpen} onClose={onWhatsNewClose}></WhatsNewModal>
      <Menu>
        <MenuButton
          as={IconButton}
          aria-label='Menu'
          icon={<FaBars />}
          position="fixed"
          top="0"
          left="0"
          // Fixed to the exact same height/margin as BaseTemplate.Header's ACTION_BAR_HEIGHT/
          // ACTION_BAR_MARGIN (not Chakra's size='lg' preset) so the two always line up and match
          // in height, regardless of either component's own content.
          h={ACTION_BAR_HEIGHT}
          w={ACTION_BAR_HEIGHT}
          minW={ACTION_BAR_HEIGHT}
          m={ACTION_BAR_MARGIN}
          zIndex="1"
        />
        <MenuList>
          {filteredOptions.map((option) =>
            option.submenu && option.submenu.length > 0 ? (
              // No nested flyout on mobile (out of scope) - group sub-items under a labelled,
              // non-interactive MenuGroup title instead, so they're not silently dropped.
              <MenuGroup title={option.name} key={option.name}>
                {option.submenu.map((item, i) =>
                  item.href ? (
                    <Link href={item.href} key={i}><MenuItem>{item.text}</MenuItem></Link>
                  ) : (
                    <MenuItem key={i} onClick={item.onClick}>{item.text}</MenuItem>
                  )
                )}
              </MenuGroup>
            ) : (
              <Link href={option.href} key={option.name}>
                <MenuItem>
                  {option.name}
                </MenuItem>
              </Link>
            )
          )}
          {/* WHATS NEW UPDATE HIDDEN ON SIDEBAR AND BURGER MENU - CHANGE BOTH */}
          <Link href="#" onClick={onWhatsNewToggle} key={"Whats New"} hidden><MenuItem>What's New?</MenuItem></Link>
        </MenuList>
      </Menu>
      <Box>
        {children}
      </Box>
    </>
  )
}

const Sidebar: React.FC<SidebarProps> = ({ children }) => {
  const { logout } = useAuth();
  const { user, customerType } = useCurrentUser();

  const showFlex = Number(user?.showFlex) > 0;

  const { isOpen: isWhatsNewOpen, onToggle: onWhatsNewToggle, onClose: onWhatsNewClose } = useDisclosure(); // tracks status of whats new modal

  const filteredOptions = menuOptions
  // Keep if not internal OR if the customer type is 'EYEBRIGHT'
  .filter(option => !option.internalCustomerOnly || customerType === 'EYEBRIGHT')
  // Keep if it's not 'Flex' OR (it is 'Flex' but showFlex is true)
  .filter(option => option.name !== 'Flex' || showFlex)
  // Keep if not explicitly hidden (testing-only pages still reachable by direct URL)
  .filter(option => !option.hidden)
  // Keep if not restricted to one email OR the signed-in user is that email
  .filter(option => !option.restrictedToEmail || user?.emailaddress?.toLowerCase() === option.restrictedToEmail.toLowerCase());

  return (
    <>
      <WhatsNewModal isOpen={isWhatsNewOpen} onClose={onWhatsNewClose}></WhatsNewModal>
      <Box>
        <Box
          display={"flex"}
          bg='navbar-bg'
          height="100vh"
          width={SIDEBAR_WIDTH}
          position="fixed"
          top="0"
          left="0"
          zIndex="100000"
          // No overflow set here (custom-scroll is cosmetic scrollbar styling only, not
          // overflow:auto) - if the nav list ever grows long enough to need scrolling, give the
          // inner list VStack its own overflowY, not this outer box, or it'll clip the flyouts.
          className='custom-scroll'
        >
          <Flex
            direction="column"
            justifyContent="space-between"
            height="100%"
          >
          <VStack align='center' spacing={0}>
            {
              filteredOptions.map((option) => (
                <NavItem
                  key={option.name}
                  href={option.href}
                  text={option.name}
                  icon={option.icon}
                  iconElement={option.iconElement}
                  color="navbar-bg"
                  submenu={option.submenu}
                />
              ))
            }
          </VStack>
          <Box marginTop={'auto'} />
          {/* WHATS NEW UPDATE HIDDEN ON SIDEBAR AND BURGER MENU - CHANGE BOTH */}
          {false &&
            <NavItem color='navbar-bg' icon={FaLightbulb} onClick={onWhatsNewToggle} text="What's New?" />
          }
          <VStack align='center' spacing={0} bg="navbar-bg-secondary">
            <NavItem color='navbar-bg-secondary' icon={FaUser} text={user?.firstname || "User"} />
            <NavItem color='navbar-bg-secondary' onClick={logout} icon={FaArrowRightFromBracket} text="Log Out" />
            <NavItem color='navbar-bg-secondary' icon={FaCircleInfo} href="/technical_details" text="Technical Details" />
            <NavItem color='navbar-bg-secondary' icon={FaPalette} href="/theme_editor" text="Theme Editor" />
          </VStack>
        </Flex>
        </Box>
        <Box backgroundColor={"app-bg"} ml={SIDEBAR_WIDTH}>
          {children}
        </Box>
      </Box>
    </>

  );
};

export default DynamicMenu;
